import React, { useCallback, useEffect, useMemo, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { MdAdd, MdEdit, MdDelete, MdLink, MdSchedule } from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import DataTable from "components/data-table";
import { Checkbox, InputField } from "components/fields";
import { EmptyState, ErrorState } from "components/states";
import { formatDate } from "utils/format";

const LINK_FORM_ID = "link-form";

const hasSchedule = (link) => Boolean(link.scheduledStart || link.scheduledEnd);

const formatDateForInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};

const LinkForm = ({ link, onSave }) => {
  const [formData, setFormData] = useState(() => ({
    title: link?.title || "",
    url: link?.url || "",
    scheduledStart: formatDateForInput(link?.scheduledStart),
    scheduledEnd: formatDateForInput(link?.scheduledEnd),
  }));
  const [showScheduling, setShowScheduling] = useState(() => Boolean(link && hasSchedule(link)));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      title: formData.title,
      url: formData.url,
      scheduledStart: showScheduling && formData.scheduledStart ? formData.scheduledStart : null,
      scheduledEnd: showScheduling && formData.scheduledEnd ? formData.scheduledEnd : null,
    });
  };

  return (
    <form id={LINK_FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
      <InputField
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        required
        placeholder="Sunday Livestream"
      />
      <InputField
        label="URL"
        type="url"
        name="url"
        value={formData.url}
        onChange={handleInputChange}
        required
        placeholder="https://example.com"
      />
      <div className="flex flex-col gap-4 rounded-control border border-line p-4">
        <Checkbox
          label="Schedule This Link"
          description="Links only show on your public page during the scheduled dates."
          checked={showScheduling}
          onChange={(e) => setShowScheduling(e.target.checked)}
        />
        {showScheduling && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InputField
              label="Start Showing From"
              hint="Optional"
              type="date"
              name="scheduledStart"
              value={formData.scheduledStart}
              onChange={handleInputChange}
            />
            <InputField
              label="Stop Showing After"
              hint="Optional"
              type="date"
              name="scheduledEnd"
              value={formData.scheduledEnd}
              onChange={handleInputChange}
            />
          </div>
        )}
      </div>
    </form>
  );
};

const scheduleText = (link) =>
  [
    link.scheduledStart && `From ${formatDate(link.scheduledStart)}`,
    link.scheduledEnd && `Until ${formatDate(link.scheduledEnd)}`,
  ]
    .filter(Boolean)
    .join(" · ");

const LinksManagement = () => {
  const confirm = useConfirm();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);

  const fetchLinks = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/connect/links");
      if (response.data.success) {
        setLinks(response.data.data || []);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const retryFetch = () => {
    setLoading(true);
    fetchLinks();
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setModalOpen(true);
  };

  const handleEditLink = useCallback((link) => {
    setEditingLink(link);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingLink(null);
  };

  const handleSaveLink = async (formData) => {
    setSaving(true);
    try {
      const response = editingLink
        ? await AtriumApi.put(`/connect/links/${editingLink.id}`, formData)
        : await AtriumApi.post("/connect/links", formData);

      if (response.data.success) {
        notify.success(editingLink ? "Link updated" : "Link added");
        closeModal();
        fetchLinks();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save link"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = useCallback(
    async (link) => {
      const confirmed = await confirm({
        title: `Delete "${link.title}"?`,
        message: "This link will be removed from your public Link Hub.",
        confirmLabel: "Delete",
        tone: "danger",
      });
      if (!confirmed) return;

      setSaving(true);
      try {
        const response = await AtriumApi.delete(`/connect/links/${link.id}`);
        if (response.data.success) {
          notify.success("Link deleted");
          fetchLinks();
        }
      } catch (error) {
        notify.error(getApiErrorMessage(error, "Could not delete link"));
      } finally {
        setSaving(false);
      }
    },
    [confirm, fetchLinks]
  );

  const columns = useMemo(
    () => [
      {
        id: "link",
        header: "Link",
        cell: ({ row }) => {
          const link = row.original;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                <MdLink aria-hidden="true" className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate font-medium text-fg">{link.title}</p>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm text-fg-muted hover:underline"
                >
                  {link.url}
                </a>
              </div>
            </div>
          );
        },
      },
      {
        id: "schedule",
        header: "Schedule",
        cell: ({ row }) => {
          const link = row.original;
          if (!hasSchedule(link)) {
            return <span className="text-sm text-fg-muted">Always visible</span>;
          }
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge tone="info">
                <MdSchedule aria-hidden="true" className="h-3 w-3" />
                Scheduled
              </Badge>
              <span className="text-xs text-fg-muted">{scheduleText(link)}</span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        meta: { align: "right" },
        cell: ({ row }) => {
          const link = row.original;
          return (
            <div className="flex justify-end gap-1">
              <IconButton
                icon={<MdEdit />}
                label={`Edit “${link.title}”`}
                onClick={() => handleEditLink(link)}
              />
              <IconButton
                icon={<MdDelete />}
                label={`Delete “${link.title}”`}
                onClick={() => handleDeleteLink(link)}
                disabled={saving}
              />
            </div>
          );
        },
      },
    ],
    [handleEditLink, handleDeleteLink, saving]
  );

  const addButton = (
    <Button leftIcon={<MdAdd />} onClick={handleAddLink} disabled={saving}>
      Add Link
    </Button>
  );

  const renderContent = () => {
    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load links")}
            onRetry={retryFetch}
          />
        </Card>
      );
    }
    if (!loading && links.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdLink}
            title="No Links Yet"
            description="Add your first link to get started."
            action={addButton}
          />
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {!loading && (
          <p className="text-sm text-fg-muted">
            {links.length} {links.length === 1 ? "link" : "links"} total
          </p>
        )}
        <DataTable
          aria-label="Links"
          columns={columns}
          data={links}
          loading={loading}
          getRowId={(link) => String(link.id)}
          pagination={links.length > 10 || undefined}
        />
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Links"
        description="Add and edit your links. Reorder them from the Customize page."
        actions={!loadError && addButton}
      />

      {renderContent()}

      <Dialog
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingLink ? "Edit Link" : "Add Link"}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form={LINK_FORM_ID} loading={saving}>
              {editingLink ? "Save Changes" : "Create Link"}
            </Button>
          </>
        }
      >
        <LinkForm link={editingLink} onSave={handleSaveLink} />
      </Dialog>
    </PageContainer>
  );
};

export default LinksManagement;
